import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { auth, firestore as db } from '../config/Config';
import Swal from 'sweetalert2';
import toast, { Toaster } from 'react-hot-toast';
import {
  User, Shield, KeyRound, Save,
  Mail, Phone, RefreshCw, Copy,
  Calendar, Clock, Lock,
  Briefcase, CheckCircle2, Eye, EyeOff, AlertCircle
} from 'lucide-react';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  // eslint-disable-next-line no-unused-vars
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [copiedUid, setCopiedUid] = useState(false);

  // Password fields and visibility toggles
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Track live auth user state
  const [authUser, setAuthUser] = useState(() => auth.currentUser);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      setAuthUser(user);
    });
    return () => unsubscribeAuth();
  }, []);

  const currentAuthUser = authUser || auth.currentUser;
  const userUID = currentAuthUser?.uid || localStorage.getItem('userUID') || '';

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    mobile: '',
    designation: '',
    location: '',
    accountStatus: 'Active',
    createdAt: '',
    lastLogin: '',
  });

  // Fetch live user profile from Firestore
  useEffect(() => {
    if (!userUID) {
      setIsLoadingUser(false);
      return;
    }
    setIsLoadingUser(true);

    const fmtDate = (t) =>
      t
        ? new Date(t).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        : '—';
    const fmtDT = (t) =>
      t
        ? new Date(t).toLocaleString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
        : '—';

    const unsubscribe = db
      .collection('users')
      .doc(userUID)
      .onSnapshot(
        (docSnap) => {
          const base = {
            createdAt: fmtDate(currentAuthUser?.metadata?.creationTime),
            lastLogin: fmtDT(currentAuthUser?.metadata?.lastSignInTime),
          };
          if (docSnap.exists) {
            const d = docSnap.data();
            setProfileData({
              name: d.name || currentAuthUser?.displayName || '',
              email: d.email || currentAuthUser?.email || '',
              mobile: d.mobile || d.number || '',
              designation: d.designation || d.role || '',
              location: d.location || '',
              accountStatus: d.status || (d.firstLoginDone ? 'Active' : 'Pending'),
              ...base,
            });
          } else {
            setProfileData({
              name: currentAuthUser?.displayName || '',
              email: currentAuthUser?.email || '',
              mobile: '',
              designation: '',
              location: '',
              accountStatus: 'Active',
              ...base,
            });
          }
          setIsLoadingUser(false);
        },
        (err) => {
          console.error('Firestore error:', err);
          setIsLoadingUser(false);
        }
      );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUID]);

  const handleCopyUid = () => {
    if (!userUID) return;
    navigator.clipboard.writeText(userUID);
    setCopiedUid(true);
    toast.success('User ID copied to clipboard!');
    setTimeout(() => setCopiedUid(false), 2500);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      toast.error('Full Name is required');
      return;
    }
    setIsSavingProfile(true);
    try {
      const updatedName = profileData.name.trim();

      // Update Firestore user document
      await db
        .collection('users')
        .doc(userUID)
        .set(
          {
            name: updatedName,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );

      // Update localStorage userDetails
      const parsed = JSON.parse(localStorage.getItem('userDetails') || '{}');
      localStorage.setItem(
        'userDetails',
        JSON.stringify({
          ...parsed,
          name: updatedName,
        })
      );

      // Update Firebase Auth profile displayName
      if (auth.currentUser) {
        await auth.currentUser.updateProfile({ displayName: updatedName });
      }

      // Notify other components (Header) to re-sync immediately
      window.dispatchEvent(new Event('storage'));

      Swal.fire({
        icon: 'success',
        title: 'Profile Updated!',
        text: 'Your name has been updated successfully.',
        confirmButtonColor: '#ea6e2b',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Please enter your current password.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password.');
      return;
    }

    const userEmail = currentAuthUser?.email || profileData.email;
    if (!userEmail) {
      toast.error('Could not identify user email. Please re-login.');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      // Re-authenticate by signing in with the current password.
      // This validates current password, mints fresh tokens (preventing 404 / stale session),
      // and satisfies Firebase recent login requirements.
      const userCredential = await auth.signInWithEmailAndPassword(userEmail, currentPassword);

      // Update the password
      await userCredential.user.updatePassword(newPassword);

      // Sync Firestore user document
      const uid = userCredential.user.uid;
      if (uid) {
        await db.collection('users').doc(uid).set(
          {
            firstLoginDone: true,
            passwordUpdatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      }

      // Update user token in localStorage
      const freshToken = await userCredential.user.getIdToken();
      localStorage.setItem('userToken', freshToken);

      Swal.fire({
        icon: 'success',
        title: 'Password Updated!',
        text: 'Your account password has been successfully updated.',
        confirmButtonColor: '#ea6e2b',
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Password update error:', err);
      if (err.code === 'auth/wrong-password') {
        Swal.fire({
          icon: 'error',
          title: 'Incorrect Current Password',
          text: 'The current password you entered is incorrect. Please check and try again.',
          confirmButtonColor: '#ea6e2b',
        });
      } else if (err.code === 'auth/user-not-found') {
        Swal.fire({
          icon: 'error',
          title: 'Account Not Found',
          text: `No active authentication account was found for ${userEmail}. Please verify your login credentials or sign in again.`,
          confirmButtonColor: '#ea6e2b',
        });
      } else if (err.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please use at least 6 characters.');
      } else if (err.code === 'auth/too-many-requests') {
        Swal.fire({
          icon: 'warning',
          title: 'Too Many Attempts',
          text: 'Access has been temporarily restricted due to multiple failed attempts. Please try again later.',
          confirmButtonColor: '#ea6e2b',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: err.message || 'Could not update password. Please check your credentials and try again.',
          confirmButtonColor: '#ea6e2b',
        });
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };


  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const displayName = profileData.name || currentAuthUser?.displayName || currentAuthUser?.email?.split('@')[0] || 'User';
  const displayRole = profileData.designation;

  return (
    <Container fluid className="p-0 bg-[#faf7f2] min-h-screen text-gray-800">
      <Toaster position="top-right" />
      <Row className="m-0">
        <Col sm={1}></Col>
        <Col sm={11} className="py-7 px-8 lg:px-12 pb-12 max-w-[1500px]">
          {/* Hero Banner */}
          <div
            className="relative rounded-[20px] overflow-hidden bg-[#f5eee6] p-8 px-10 mb-6 min-h-[150px] flex items-center justify-between border border-[#ebd9c8] shadow-[0_2px_14px_rgba(0,0,0,0.03)] bg-no-repeat"
            style={{
              backgroundImage: `linear-gradient(to right, #f5eee6 0%, #f5eee6 36%, rgba(245,238,230,0.85) 60%, rgba(245,238,230,0.1) 100%), url(${process.env.PUBLIC_URL + '/settings/hero_living_room.jpg'})`,
              backgroundPosition: 'right center',
              backgroundSize: 'auto 100%, 52% auto',
            }}
          >
            <div className="max-w-[460px] z-10">
              <h1 className="font-serif text-[30px] lg:text-[32px] font-bold text-[#1a1f26] mb-1.5 tracking-tight">
                Settings
              </h1>
              <p className="text-[13px] text-[#555e6c] m-0 leading-relaxed">
                Manage your profile, credentials, and security preferences.
              </p>
            </div>

            {/* Centered Quote on Banner */}
            <div className="relative z-10 hidden md:block text-left mr-8 lg:mr-24">
              <div className="font-serif italic text-[15px] text-gray-700 leading-snug font-medium">
                “Great Interiors<br />Start with Great People”
              </div>
              <div className="w-8 h-[2.5px] bg-[#ea6e2b] mt-1.5 rounded-sm" />
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('profile')}
              className={`inline-flex items-center gap-2 py-2.5 px-6 rounded-xl text-[12.5px] font-bold cursor-pointer transition-all duration-200 border ${
                activeTab === 'profile'
                  ? '!bg-[#ea6e2b] !text-white !border-transparent shadow-[0_3px_10px_rgba(234,110,43,0.3)]'
                  : '!bg-white !text-gray-600 !border-gray-200 shadow-sm hover:!bg-gray-50 hover:!text-gray-900'
              }`}
            >
              <User size={15} color={activeTab === 'profile' ? '#ffffff' : '#ea6e2b'} />
              <span>Profile &amp; Account</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('security')}
              className={`inline-flex items-center gap-2 py-2.5 px-6 rounded-xl text-[12.5px] font-bold cursor-pointer transition-all duration-200 border ${
                activeTab === 'security'
                  ? '!bg-[#ea6e2b] !text-white !border-transparent shadow-[0_3px_10px_rgba(234,110,43,0.3)]'
                  : '!bg-white !text-gray-600 !border-gray-200 shadow-sm hover:!bg-gray-50 hover:!text-gray-900'
              }`}
            >
              <Lock size={15} color={activeTab === 'security' ? '#ffffff' : '#6b7280'} />
              <span>Security &amp; Password</span>
            </button>
          </div>

          {/* Tab 1: Profile & Account */}
          {activeTab === 'profile' && (
            <Row className="g-4">
              {/* Left Column: Profile Card */}
              <Col lg={4} md={5} sm={12}>
                <div className="bg-white rounded-[20px] border border-[#ece6dc] shadow-[0_2px_12px_rgba(0,0,0,0.03)] overflow-hidden flex flex-col h-full">
                  {/* Top illustration section */}
                  <div
                    className="relative h-[135px] bg-[#f8f4ee] p-6 flex flex-col justify-center bg-no-repeat"
                    style={{
                      backgroundImage: `linear-gradient(to right, #f8f4ee 40%, rgba(248,244,238,0.7) 65%, transparent 100%), url(${process.env.PUBLIC_URL + '/settings/armchair_card.jpg'})`,
                      backgroundPosition: 'right center',
                      backgroundSize: 'auto 100%, 58% auto',
                    }}
                  >
                    <div className="font-serif text-[#2d333e] text-[15px] leading-tight">
                      Create<br />
                      <span className="text-[#ea6e2b] italic font-semibold">Beautiful</span><br />
                      Spaces
                    </div>
                    <div className="w-6 h-[2.5px] bg-[#ea6e2b] mt-1.5 rounded-sm" />
                  </div>

                  {/* Avatar Circle */}
                  <div className="w-[78px] h-[78px] rounded-full bg-[#ea6e2b] text-white text-[28px] font-bold flex items-center justify-center border-4 border-white shadow-[0_4px_14px_rgba(234,110,43,0.3)] -mt-[39px] mx-auto z-10 select-none">
                    {getInitials(displayName)}
                  </div>

                  {/* User Profile Summary Details */}
                  <div className="pt-2.5 px-6 pb-6 text-center flex-1 flex flex-col justify-between">
                    <div>
                      <h2 className="text-[18px] font-bold text-gray-900 mt-1 mb-0.5">
                        {displayName}
                      </h2>
                      {displayRole && (
                        <div className="text-[12.5px] text-gray-500 mb-4 font-medium">
                          {displayRole}
                        </div>
                      )}
                      {!displayRole && <div className="mb-4" />}

                      {/* USER ID Box */}
                      <div className="bg-slate-50 rounded-xl p-3 px-3.5 border border-slate-200 text-left mb-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-bold text-slate-500 tracking-wider uppercase">
                            ::: USER ID
                          </span>
                          <button
                            type="button"
                            onClick={handleCopyUid}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-md py-0.5 px-2 cursor-pointer transition-colors !bg-white !text-[#ea6e2b] !border !border-orange-200 hover:!bg-orange-50"
                          >
                            <Copy size={11} /> {copiedUid ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-lg py-1.5 px-2.5 font-mono text-[11.5px] text-slate-700 overflow-hidden text-ellipsis whitespace-nowrap">
                          {userUID || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Meta Attributes */}
                    <div className="flex flex-col gap-2.5 text-left text-[12.5px]">
                      <div className="flex items-center justify-between p-2.5 px-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                        <span className="flex items-center gap-2 text-slate-500 font-medium">
                          <Mail size={14} className="text-slate-400" /> Email
                        </span>
                        <span className="font-semibold text-slate-800 text-right truncate max-w-[170px]" title={profileData.email || currentAuthUser?.email}>
                          {profileData.email || currentAuthUser?.email || '—'}
                        </span>
                      </div>

                      {profileData.mobile && (
                        <div className="flex items-center justify-between p-2.5 px-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                          <span className="flex items-center gap-2 text-slate-500 font-medium">
                            <Phone size={14} className="text-slate-400" /> Mobile
                          </span>
                          <span className="font-semibold text-slate-800 font-mono">
                            {profileData.mobile}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between p-2.5 px-3.5 rounded-xl bg-slate-50 border border-slate-200/70">
                        <span className="flex items-center gap-2 text-slate-500 font-medium">
                          <CheckCircle2 size={14} className="text-emerald-500" /> Status
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {profileData.accountStatus || 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Right Column: Personal Information Form */}
              <Col lg={8} md={7} sm={12}>
                <div className="bg-white rounded-[20px] border border-[#ece6dc] shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 px-9 flex flex-col justify-between h-full">
                  <div>
                    {/* Card Header & Quote Badge */}
                    <div className="flex items-center justify-between mb-7">
                      <div className="flex items-center gap-3.5">
                        <div className="w-[42px] h-[42px] rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100 text-[#ea6e2b]">
                          <User size={20} />
                        </div>
                        <div>
                          <h2 className="text-[17px] font-bold text-gray-900 m-0">Personal Information</h2>
                          <p className="text-[12px] text-slate-500 mt-0.5 mb-0">Update your personal profile details stored in the CRM</p>
                        </div>
                      </div>

                      {/* Quote badge on right */}
                      <div className="bg-[#fef3eb] rounded-full py-1.5 px-3.5 flex items-center gap-1.5 border border-orange-200">
                        <span className="font-serif italic text-[12px] text-[#9a3412] font-medium">
                           Same People. Better Spaces.
                        </span>
                      </div>
                    </div>

                    {/* Form Inputs Grid: Only Name is editable */}
                    <form onSubmit={handleSaveProfile}>
                      <Row className="g-3.5">
                        {/* Editable Full Name */}
                        <Col sm={12}>
                          <div className="mb-4">
                            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                              FULL NAME <span className="text-red-500">*</span>
                            </label>
                            <div className="relative flex items-center">
                              <User size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                              <input
                                type="text"
                                value={profileData.name}
                                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                placeholder="Enter Your Name"
                                required
                                className="w-full py-2.5 pr-3.5 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-800 bg-white outline-none transition-all duration-200 focus:border-[#ea6e2b] focus:ring-1 focus:ring-[#ea6e2b]"
                              />
                            </div>
                            <span className="text-[11px] text-slate-400 mt-1 block">
                              This name will appear on your quotations, client interactions, and system header.
                            </span>
                          </div>
                        </Col>

                        {/* Read-Only Registered Email */}
                        <Col sm={6}>
                          <div className="mb-4">
                            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                              REGISTERED EMAIL
                            </label>
                            <div className="relative flex items-center">
                              <Mail size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                              <input
                                type="email"
                                value={profileData.email || currentAuthUser?.email || '—'}
                                readOnly
                                disabled
                                className="w-full py-2.5 pr-3.5 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-500 bg-slate-50 cursor-not-allowed outline-none"
                              />
                            </div>
                          </div>
                        </Col>

                        {/* Read-Only Phone / Mobile (Visible but not editable) */}
                        <Col sm={6}>
                          <div className="mb-4">
                            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                              PHONE / MOBILE
                            </label>
                            <div className="relative flex items-center">
                              <Phone size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                              <input
                                type="tel"
                                value={profileData.mobile || '—'}
                                readOnly
                                disabled
                                className="w-full py-2.5 pr-3.5 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-500 bg-slate-50 cursor-not-allowed outline-none font-mono"
                              />
                            </div>
                          </div>
                        </Col>

                        {/* Read-Only Designation / Role */}
                        <Col sm={12}>
                          <div className="mb-4">
                            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                              DESIGNATION / ROLE
                            </label>
                            <div className="relative flex items-center">
                              <Briefcase size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                              <input
                                type="text"
                                value={profileData.designation || 'Team Member'}
                                readOnly
                                disabled
                                className="w-full py-2.5 pr-3.5 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-500 bg-slate-50 cursor-not-allowed outline-none"
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      {/* Action buttons: Cancel removed, Save button aligned right and working */}
                      <div className="flex items-center justify-end pt-5 mt-2 border-t border-slate-100">
                        <button
                          type="submit"
                          disabled={isSavingProfile}
                          className={`inline-flex items-center gap-2 py-2.5 px-7 rounded-xl text-[12.5px] font-bold !bg-[#ea6e2b] hover:!bg-[#d45e1f] !text-white !border !border-[#ea6e2b] shadow-[0_3px_12px_rgba(234,110,43,0.35)] transition-all duration-200 ${
                            isSavingProfile ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:shadow-[0_4px_16px_rgba(234,110,43,0.45)]'
                          }`}
                        >
                          {isSavingProfile ? (
                            <RefreshCw size={14} className="animate-spin text-white" />
                          ) : (
                            <Save size={14} className="text-white" />
                          )}
                          <span className="text-white font-bold">
                            {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
                          </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Tab 2: Security & Password */}
          {activeTab === 'security' && (
            <Row className="g-4">
              {/* Left Column: Change Password Card */}
              <Col lg={7} md={12}>
                <div className="bg-white rounded-[20px] border border-[#ece6dc] shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-8 px-9 flex flex-col justify-between h-full">
                  <div>
                    {/* Header */}
                    <div className="flex items-center gap-3.5 mb-6">
                      <div className="w-[42px] h-[42px] rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100 text-[#ea6e2b]">
                        <KeyRound size={20} />
                      </div>
                      <div>
                        <h2 className="text-[17px] font-bold text-gray-900 m-0">Change Account Password</h2>
                        <p className="text-[12px] text-slate-500 mt-0.5 mb-0">Update your login credentials via Firebase Authentication</p>
                      </div>
                    </div>

                    <form onSubmit={handleUpdatePassword}>
                      {/* Current Password */}
                      <div className="mb-4">
                        <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                          CURRENT PASSWORD <span className="text-red-500">*</span>
                        </label>
                        <div className="relative flex items-center">
                          <Lock size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter your current password"
                            required
                            className="w-full py-2.5 pr-10 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-800 bg-white outline-none transition-all duration-200 focus:border-[#ea6e2b] focus:ring-1 focus:ring-[#ea6e2b]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer p-0 flex items-center"
                            title={showCurrentPassword ? 'Hide password' : 'Show password'}
                          >
                            {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <span className="text-[11px] text-slate-400 mt-1 block">
                          Required to verify your identity before updating credentials.
                        </span>
                      </div>

                      {/* New Password */}
                      <div className="mb-4">
                        <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                          NEW PASSWORD <span className="text-red-500">*</span>
                        </label>
                        <div className="relative flex items-center">
                          <Lock size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password (min. 6 characters)"
                            required
                            minLength={6}
                            className="w-full py-2.5 pr-10 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-800 bg-white outline-none transition-all duration-200 focus:border-[#ea6e2b] focus:ring-1 focus:ring-[#ea6e2b]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer p-0 flex items-center"
                            title={showNewPassword ? 'Hide password' : 'Show password'}
                          >
                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <span className="text-[11px] text-slate-400 mt-1 block">
                          Must be at least 6 characters. Use letters, numbers, and symbols for better security.
                        </span>
                      </div>

                      {/* Confirm New Password */}
                      <div className="mb-5">
                        <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                          CONFIRM NEW PASSWORD <span className="text-red-500">*</span>
                        </label>
                        <div className="relative flex items-center">
                          <Lock size={15} className="absolute left-3.5 text-slate-400 pointer-events-none" />
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-type new password"
                            required
                            minLength={6}
                            className="w-full py-2.5 pr-10 pl-10 rounded-xl border border-slate-200 text-[13px] text-slate-800 bg-white outline-none transition-all duration-200 focus:border-[#ea6e2b] focus:ring-1 focus:ring-[#ea6e2b]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer p-0 flex items-center"
                            title={showConfirmPassword ? 'Hide password' : 'Show password'}
                          >
                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>

                        {/* Password match indicator */}
                        {confirmPassword && (
                          <div className="mt-1.5 flex items-center gap-1 text-[11.5px]">
                            {newPassword === confirmPassword ? (
                              <span className="text-emerald-600 flex items-center gap-1 font-medium">
                                <CheckCircle2 size={13} /> Passwords match
                              </span>
                            ) : (
                              <span className="text-rose-500 flex items-center gap-1 font-medium">
                                <AlertCircle size={13} /> Passwords do not match
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center justify-end pt-5 border-t border-slate-100">
                        <button
                          type="submit"
                          disabled={
                            isUpdatingPassword ||
                            !currentPassword ||
                            !newPassword ||
                            newPassword.length < 6 ||
                            (confirmPassword && newPassword !== confirmPassword)
                          }
                          className={`inline-flex items-center gap-2 py-2.5 px-7 rounded-xl text-[12.5px] font-bold !bg-[#ea6e2b] hover:!bg-[#d45e1f] !text-white !border !border-[#ea6e2b] shadow-[0_3px_12px_rgba(234,110,43,0.35)] transition-all duration-200 ${
                            isUpdatingPassword ||
                            !currentPassword ||
                            !newPassword ||
                            newPassword.length < 6 ||
                            (confirmPassword && newPassword !== confirmPassword)
                              ? 'cursor-not-allowed opacity-75'
                              : 'cursor-pointer hover:shadow-[0_4px_16px_rgba(234,110,43,0.45)]'
                          }`}
                        >
                          {isUpdatingPassword ? (
                            <RefreshCw size={14} className="animate-spin text-white" />
                          ) : (
                            <Lock size={14} className="text-white" />
                          )}
                          <span className="text-white font-bold">
                            {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                          </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Col>

              {/* Right Column: Security Overview & Status */}
              <Col lg={5} md={12}>
                <div className="bg-white rounded-[20px] border border-[#ece6dc] shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-7 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center justify-between pb-3.5 border-b border-slate-100 mb-4">
                      <div className="flex items-center gap-2 text-[#ea6e2b]">
                        <Shield size={18} />
                        <h3 className="text-[15px] font-bold text-gray-900 m-0">Security Overview</h3>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 size={12} /> Active
                      </span>
                    </div>

                    <div className="flex flex-col gap-3">
                      {[
                        { icon: <Clock size={13} className="text-slate-500" />, label: 'Last Signed In', value: profileData.lastLogin || '—' },
                        { icon: <Calendar size={13} className="text-slate-500" />, label: 'Member Since', value: profileData.createdAt || '—' },
                        { icon: <Shield size={13} className="text-slate-500" />, label: 'Authentication Provider', value: 'Firebase Secure Auth' },
                        { icon: <Mail size={13} className="text-slate-500" />, label: 'Account Email', value: profileData.email || currentAuthUser?.email || '—' },
                      ].map((item, i) => (
                        <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 p-3 px-3.5">
                          <span className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1 font-medium">
                            {item.icon} {item.label}
                          </span>
                          <span className="text-[12.5px] font-bold text-slate-800 block break-all">
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendation Card */}
                  <div className="mt-5 p-3.5 rounded-xl bg-orange-50/60 border border-orange-100 text-[11.5px] text-orange-950 flex items-start gap-2.5">
                    <Shield size={16} className="text-[#ea6e2b] flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold block mb-0.5">Password Best Practices</span>
                      Choose a unique password you don't use anywhere else. Minimum 6 characters required.
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Footer */}
          <footer className="flex items-center justify-between pt-9 pb-4 mt-9 border-t border-[#e8e2d8] text-[11.5px] text-slate-400">
            <div>© {new Date().getFullYear()} Create Quote. All rights reserved.</div>
          </footer>
        </Col>
      </Row>
    </Container>
  );
}

